import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Snowfall } from "./JapaneseMotifs";

/* Japon Kapisi — siteye giris deneyimi. Oturumda bir kez gosterilir.
   Ses TEK kanaldan gelir: kapida konusma yok; Tudi'nin selami (ChatWidget) tek basina calar.
   Master yonerge: zorunlu tiklama YOK — kapi 3 saniyede kendiliginden zarifce acilir.
   Onizleme: ?season=spring|summer|autumn|winter|newyear */

export function gateSeason() {
  try {
    const p = new URLSearchParams(window.location.search).get("season");
    if (["spring", "summer", "autumn", "winter", "newyear"].includes(p)) return p;
  } catch {}
  const m = new Date().getMonth();
  if (m === 11) return "newyear";
  return m <= 1 ? "winter" : m <= 4 ? "spring" : m <= 7 ? "summer" : "autumn";
}

export function IntroGate() {
  const { t } = useLang();
  const [season] = useState(gateSeason);
  const snowy = season === "winter" || season === "newyear";
  // Lazy init: render fazinda karar verilir — Tudi tur bayragi (tudi_tour_pending) ChatWidget efektinden once hazir olur.
  // Tarayici botlarina ve arama motorundan gelen ziyaretciye kapi GOSTERILMEZ (SEO: interstitial cezasi + icerik erisimi)
  const [show, setShow] = useState(() => {
    try {
      if (/bot|crawler|spider|slurp|facebookexternalhit|twitterbot|linkedinbot|whatsapp|headless/i.test(navigator.userAgent)) return false;
      if (/google\.|bing\.|yandex\.|duckduckgo|yandex\./i.test(document.referrer || "")) return false;
      if (sessionStorage.getItem("intro_seen")) return false;
      sessionStorage.setItem("tudi_tour_pending", "1");
      return true;
    } catch { return true; }
  });
  const [opening, setOpening] = useState(false);

  const dismiss = () => {
    if (opening) return;
    setOpening(true);
    setTimeout(() => {
      try { sessionStorage.setItem("intro_seen", "1"); } catch {}
      setShow(false);
    }, 1000);
  };

  // Zorunlu tiklama yok: kapi 3 saniye sonra kendiliginden acilir (kullanici isterse butonla hemen gecer)
  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(dismiss, 3000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="intro-gate"
          className="fixed inset-0 z-[200] bg-[#0A2138] text-white overflow-hidden flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.4 } }}
        >
          {/* Fuji sahnesi — ust (acilis animasyonunda hafif yakinlasir) */}
          <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: opening ? 0 : 1, scale: opening ? 1.12 : 1 }}
            transition={{ duration: opening ? 1 : 1.4, ease: "easeOut" }}
          >
            <img src="/img/intro/fuji-scene.png" alt="" className="w-full h-[38vh] sm:h-[42vh] object-cover object-center" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0A2138] to-transparent" />
          </motion.div>
          {snowy && <Snowfall />}

          {/* Torii kapilari — "Siteye Gir"de iki yana acilir */}
          <motion.img
            src="/img/intro/torii.png" alt="" aria-hidden="true"
            className="absolute left-2 sm:left-8 bottom-6 h-[20vh] sm:h-[46vh] pointer-events-none"
            initial={{ opacity: 0, x: -30 }}
            animate={opening ? { opacity: 0, x: "-60vw" } : { opacity: 0.7, x: 0 }}
            transition={opening ? { duration: 0.9, ease: [0.7, 0, 0.2, 1] } : { delay: 0.6, duration: 1 }}
          />
          <motion.img
            src="/img/intro/torii.png" alt="" aria-hidden="true"
            className="absolute right-2 sm:right-8 bottom-6 h-[20vh] sm:h-[46vh] pointer-events-none -scale-x-100"
            initial={{ opacity: 0, x: 30 }}
            animate={opening ? { opacity: 0, x: "60vw" } : { opacity: 0.7, x: 0 }}
            transition={opening ? { duration: 0.9, ease: [0.7, 0, 0.2, 1] } : { delay: 0.6, duration: 1 }}
          />

          {/* Büyük Japonca harfler — kenarlarda dikey */}
          <motion.span aria-hidden="true" className="jp-vertical hidden md:block absolute left-6 top-[8vh] font-heading text-4xl lg:text-5xl tracking-[0.35em] text-white/25 select-none"
            animate={opening ? { opacity: 0, y: -40 } : { opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            信頼
          </motion.span>
          <motion.span aria-hidden="true" className="jp-vertical hidden md:block absolute right-6 top-[8vh] font-heading text-2xl lg:text-3xl tracking-[0.35em] text-white/20 select-none"
            animate={opening ? { opacity: 0, y: 40 } : { opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            日本のがん治療
          </motion.span>

          {/* Orta içerik */}
          <motion.div
            className="relative flex-1 flex flex-col items-center justify-center text-center px-6 -mt-6"
            animate={opening ? { opacity: 0, scale: 0.94 } : { opacity: 1, scale: 1 }}
            transition={{ duration: opening ? 0.5 : 0.4 }}
          >
            <motion.img
              src="/img/intro/japan-cells.png" alt="Japan map with cells"
              data-testid="intro-mascot"
              className="w-36 sm:w-48 object-contain drop-shadow-[0_18px_50px_rgba(160,211,232,0.45)]"
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.9 }}
            />
            <motion.p
              className="mt-5 text-[10px] sm:text-xs font-bold tracking-[0.35em] uppercase text-aqua"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
            >
              {t("intro_kicker")}
            </motion.p>
            <motion.h1
              className="mt-3 font-heading text-2xl sm:text-4xl lg:text-5xl font-bold leading-tight"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.8 }}
            >
              {t("intro_title")}
            </motion.h1>
            <motion.p
              className="mt-3 text-sm sm:text-base text-white/65 max-w-xl"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
            >
              {t("intro_sub")}
            </motion.p>
            <motion.button
              data-testid="intro-enter-button"
              onClick={dismiss}
              className="mt-6 inline-flex items-center gap-2 bg-brand hover:bg-[#0E76C9] text-white font-semibold text-sm sm:text-base px-8 py-3.5 rounded-full transition-colors shadow-[0_10px_40px_rgba(30,144,255,0.35)]"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95 }}
            >
              {t("intro_enter")} <ArrowRight size={18} />
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
